import { useState } from 'react';
import { motion } from 'motion/react';
import { gameLabel } from '@redpandev/shared';
import { Badge, Button, Card, Mascot, Spinner, fadeInUp, staggerContainer } from '@redpandev/ui';
import type { ActiveMatch } from '../../lib/api';
import { LinkButton } from './LinkButton';
import { formatRelativeDate } from './relativeTime';

const STATUS: Record<string, { label: string; intent: 'success' | 'warning' | 'neutral' }> = {
  waiting: { label: 'En attente', intent: 'warning' },
  active: { label: 'En cours', intent: 'success' },
};

/**
 * Parties où je suis joueur (hôte/admis) : l'action n°1 du repaire.
 * Cartes proéminentes + bouton « Reprendre » (lien primaire glow).
 * `limit` : tronque avec « Voir plus » — seulement si ≥ 2 cachées (spec 08 §3).
 */
export function ActiveMatchesList({
  matches,
  limit,
}: {
  matches: ActiveMatch[] | null;
  limit?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [announce, setAnnounce] = useState('');

  if (matches === null) {
    return <Spinner label="Chargement des parties…" className="mx-auto" />;
  }

  if (matches.length === 0) {
    return (
      <Card className="flex items-center gap-4">
        <Mascot mood="thinking" size={56} animated={false} />
        <p className="text-sm text-ink-400">
          Aucune partie en cours. Le panda tourne en rond dans son repaire…
        </p>
      </Card>
    );
  }

  const hiddenCount = limit !== undefined && !expanded ? matches.length - limit : 0;
  const truncated = hiddenCount >= 2;
  const visible = truncated ? matches.slice(0, limit) : matches;

  return (
    <div>
      <motion.ul
        variants={staggerContainer()}
        initial="hidden"
        animate="visible"
        className="space-y-3"
      >
        {visible.map((m) => {
          const status = STATUS[m.status] ?? { label: m.status, intent: 'neutral' as const };
          return (
            <motion.li key={m.matchId} variants={fadeInUp}>
              <Card
                variant="raised"
                className="flex flex-wrap items-center gap-x-4 gap-y-3 border-panda-500/30 p-4"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-lg font-bold uppercase text-panda-400">
                      {gameLabel(m.game)}
                    </span>
                    <Badge intent={status.intent} pulse={m.status === 'active'}>
                      {status.label}
                    </Badge>
                  </p>
                  <p className="mt-1 text-sm text-ink-400">
                    vs <b className="text-ink-100">{m.opponent?.displayName ?? 'en attente…'}</b>
                    {' · '}Code{' '}
                    <span className="font-mono font-bold tracking-widest text-ink-100">
                      {m.joinCode}
                    </span>
                    {' · '}
                    <time dateTime={m.createdAt}>{formatRelativeDate(m.createdAt)}</time>
                  </p>
                </div>
                <LinkButton
                  to={`/${m.game}/m/${m.matchId}`}
                  className="w-full shadow-glow-panda sm:w-auto"
                  aria-label={`Reprendre la partie ${gameLabel(m.game)} (code ${m.joinCode})`}
                >
                  Reprendre →
                </LinkButton>
              </Card>
            </motion.li>
          );
        })}
      </motion.ul>
      {truncated && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-3"
          aria-label={`Afficher les ${hiddenCount} parties supplémentaires`}
          onClick={() => {
            setExpanded(true);
            setAnnounce(`${hiddenCount} parties supplémentaires affichées`);
          }}
        >
          Voir plus ({hiddenCount})
        </Button>
      )}
      <p role="status" aria-live="polite" className="sr-only">
        {announce}
      </p>
    </div>
  );
}
